import { useCallback, useState } from 'react';
import DetailDrawer, { DRAWER_WIDTH } from './DetailDrawer';
import TicketDetailBody from './TicketDetailBody';

export interface TicketDetailDrawerProps {
  visible: boolean;
  /** 要展示详情的工单 id（null/undefined 不加载） */
  ticketId?: number | null;
  onClose: () => void;
  /** 详情内写操作（状态流转/转派/提级/切换流程/提交审批）完成后回调，供父页刷新列表 */
  onChanged?: () => void;
}

/**
 * 工单详情侧滑抽屉（工单管理页与呼叫记录页共用，保证两处详情完全一致）：
 * 薄壳 = DetailDrawer + TicketDetailBody（完整内容/操作/弹层均在 Body，
 * 待办中心流程抽屉直接复用 Body 实现单抽屉分栏）。
 * 底部操作条固定在抽屉 footer（正文随抽屉滚动、按钮始终可见）：Body 经 onOpsActive 通知
 * 何时挂载 footer 容器，操作条以 Portal 渲染进该容器。
 */
export default function TicketDetailDrawer({ visible, ticketId, onClose, onChanged }: TicketDetailDrawerProps) {
  // 标题随详情加载更新：Body 内部持有 detail，此处仅用 ticketId 占位（与旧实现标题行为一致）
  const [title, setTitle] = useState('');
  // 操作条是否可见（detail 加载完成后才 true，避免空 footer 白条）
  const [opsActive, setOpsActive] = useState(false);
  // footer 挂载区 DOM：Portal 目标，callback ref 触发重渲保证 Body 拿到最新节点
  const [opsEl, setOpsEl] = useState<HTMLElement | null>(null);
  const opsRef = useCallback((node: HTMLElement | null) => setOpsEl(node), []);

  return (
    <DetailDrawer
      // 工单详情字段多（固定字段 16 + 动态业务字段可达十余项）+ 沟通/附件/关联三个页签，
      // 按规模取 wide 档（680 档下两列描述每格只剩 ~230px，长值频繁折行）
      width={DRAWER_WIDTH.wide}
      title={title || (ticketId ? `工单 #${ticketId}` : '工单详情')}
      visible={visible}
      onCancel={onClose}
      footer={
        opsActive ? (
          // position: relative 不可省：该节点既是操作条的 Portal 目标，也是操作条弹层（如「更多」下拉）
          // 的挂载容器——弹层按本节点定位，脱离其定位上下文会错位到抽屉外。
          <div ref={opsRef} style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }} />
        ) : null
      }
    >
      <TicketDetailBody
        ticketId={ticketId}
        onClose={onClose}
        onChanged={onChanged}
        onTitle={(t: string) => setTitle(t)}
        onOpsActive={setOpsActive}
        opsTarget={opsEl}
      />
    </DetailDrawer>
  );
}
